import React from 'react';
import css from './AdvicePanel.module.scss';
import { REVIEW_AND_APPLY } from './Introduction';
import type { IdentifiedFinding } from '../../../../dorian-shared/types/episodeScript';

/**
 * T17-08: what the control on a suggestion card does, in the words of what it does.
 *
 * It said "Apply", which is the word the header uses for sending the episode's changes to the
 * engine. This control does neither: it rewrites one line of the script, and the writer still has
 * to review and apply afterwards. The sentence under it says so, in the instruction's own words.
 */
export const USE_THIS_SUGGESTION = 'Use this suggestion';

/** What happens after a suggestion is taken. It is the first instruction's own second sentence. */
export const AFTER_A_SUGGESTION = REVIEW_AND_APPLY;

/** The fixed sentence above the list (spec §9.4). */
export const ADVICE_SENTENCE = 'Suggestions are just suggestions. '
  + 'Apply shows up only where Basil can make the change for you.';

/** P6: what the panel is called. "Tips" said nothing about what was in it. */
export const SUGGESTIONS = 'Suggestions';

/** How many there are, counted rather than assumed. */
export function suggestionCount(count: number): string {
  return `${count} suggestion${count === 1 ? '' : 's'}`;
}

/**
 * The rule that is a neutral observation rather than a problem (P6).
 *
 * It is cut from the panel on FIRST OPEN — before the writer has touched anything — because a
 * writer who has just opened their own episode should not be met by a list of things they are
 * supposed to have done wrong. It comes back the moment they edit, where it is a real reminder.
 */
export const NEUTRAL_RULES: readonly string[] = ['R3'];

/**
 * The headline each rule leads with: what is wrong, in plain words. The rule code follows in small
 * type for anyone tracing it back to the design rules (editor.js:441-451).
 */
export const HEADLINES: Readonly<Record<string, string>> = Object.freeze({
  R1: 'This choice leads nowhere',
  R2: "This price isn't one the season uses",
  // P6: a missing link is not proof of a mistake. A scene can end on purpose, and the old headline
  // told the writer they had broken something they had not.
  R3: 'No next scene is linked.',
  R4: 'Nothing leads to this scene',
  R5: "Basil doesn't know this name",
  R6: 'Every way forward costs hearts',
  R7: 'Basil is still asking about this line',
  R8: 'This line is too long for the screen',
});

/**
 * The deterministic advice sentence per rule (editor.js:419-428). It is a frozen lookup, never a
 * generated phrase: the layer that would phrase these instead is a follow-up and is display-only by
 * contract. Apply travels by `findingId` and by nothing derived from this text.
 */
export const OPINIONS: Readonly<Record<string, string>> = Object.freeze({
  R1: 'Send it to a scene, or let the scene below catch the reader.',
  R2: "Use one of the season's prices so every offer reads the same way.",
  R3: 'Decide whether this is an ending, or send the scene onward.',
  R4: 'Link it from an earlier choice so a reader can get here.',
  R5: 'Use a name the book already has, or ask the owner to add this one.',
  R6: 'Keep one way forward free, so nobody is stuck unless they pay.',
  R7: "Answer Basil's question, then delete that line.",
  R8: 'Tighten the wording and keep the choice intact.',
});

/** The words on one card: the finding's own evidence, then the deterministic advice. */
export function advicePhrase(finding: IdentifiedFinding): string {
  const advice = OPINIONS[finding.rule] ?? finding.suggestion ?? '';

  return [finding.message, advice].filter(Boolean).join(' ');
}

/**
 * T17-06: the missing-name guidance no longer lives here.
 *
 * It is not a suggestion, so it is not in the panel that collapses; it sits beside the label it is
 * about, in the margin. Its type and its headings are in `viewModel/publicNames`, with the
 * sentences themselves.
 */
export type { NameGap } from '../viewModel/publicNames';
export { NAME_GAP_HEADLINE } from '../viewModel/publicNames';

/** What the advice surface needs. */
export type AdvicePanelProps = {
  /** One card per finding, in `analyse`'s own order: line, then rule. */
  findings: IdentifiedFinding[];
  /** Every variable the book has no word for; they collapse into ONE closed note. */
  unnamedVariables: string[];
  /** The scene as the script spells it, for the line the finding cites. */
  sceneNameForLine: (line: number | null) => string | null;
  /** Apply a mechanical fix. Travels by `findingId` only. */
  onApply: (findingId: string) => void;
  /** Put the caret on the finding's line. New behaviour: the prototype's cards were not clickable. */
  onGoToLine: (line: number) => void;
  /** A live episode reads its advice but applies nothing. */
  isReadOnly?: boolean;
  /**
   * T17-06: what this list is called, for a reader and for a screen reader.
   *
   * There are two of these now. The optional suggestions live behind a fold and keep the panel's
   * own name; the things a writer must see are a separate list outside it, and a list a writer is
   * told they must act on is not called "Suggestions".
   */
  label?: string;
  /** Whether to show the fixed sentence about suggestions. False for the blocking list. */
  showFixedSentence?: boolean;
  /** Whether to say "All clear" on an empty list. False where an empty list is simply absent. */
  showAllClear?: boolean;
  /**
   * T18-03: the word on each row, for a list that is not a list of suggestions.
   *
   * The blocking list is read first and acted on; calling every row in it "Suggestion" under a
   * heading that says "Needs attention" tells a writer two different things about the same card.
   */
  rowWord?: string;
};

/**
 * One card per finding: the headline, the deterministic phrasing, the rule code, the scene as the
 * script spells it, and the line. Apply renders only where `finding.mechanical` is true — R2 and R7.
 *
 * No id reaches a writer here. Every unnamed variable collapses into one closed note that counts
 * them all, and the ids live inside it and nowhere else on the surface.
 */
export function AdvicePanel(props: AdvicePanelProps) {
  const {
    findings,
    unnamedVariables,
    sceneNameForLine,
    onApply,
    onGoToLine,
    isReadOnly,
    label = SUGGESTIONS,
    showFixedSentence = true,
    showAllClear = true,
    rowWord = 'Suggestion',
  } = props;
  const many = unnamedVariables.length !== 1;

  return (
    <section className={css.list} aria-label={label}>
      {showFixedSentence ? <p className={css.fixedSentence}>{ADVICE_SENTENCE}</p> : null}
      {unnamedVariables.length > 0 ? (
        <details className={css.namesNote} data-testid="basil-names-note">
          <summary>
            {`${unnamedVariables.length} variable${many ? 's' : ''} without a name`}
          </summary>
          <p>
            {`Basil left ${many ? 'them' : 'it'} out of the words on this page. Give ${many ? 'them' : 'it'} a name in the book and ${many ? 'they' : 'it'} will read as ${many ? 'names' : 'a name'} here.`}
          </p>
          <span className={css.ids}>{unnamedVariables.join(', ')}</span>
        </details>
      ) : null}
      {findings.map((finding) => {
        const scene = sceneNameForLine(finding.line ?? null) ?? finding.sceneName ?? 'script';

        return (
          <article className={css.card} key={finding.findingId} data-severity={finding.severity}>
            <h3 className={css.headline}>{HEADLINES[finding.rule] ?? 'Worth another look'}</h3>
            <p className={css.phrase}>{advicePhrase(finding)}</p>
            <div className={css.sceneLine}>
              <span className={css.ruleCode}>{rowWord}</span>
              <button
                type="button"
                className={css.lineLink}
                onClick={() => onGoToLine(finding.line ?? 1)}
                disabled={finding.line == null}
              >
                {`${scene} · line ${finding.line ?? '—'}`}
              </button>
            </div>
            {finding.mechanical && !isReadOnly ? (
              <>
                <button
                  type="button"
                  className={css.applyButton}
                  onClick={() => onApply(finding.findingId)}
                >
                  {USE_THIS_SUGGESTION}
                </button>
                <p className={css.afterSuggestion}>{AFTER_A_SUGGESTION}</p>
              </>
            ) : null}
          </article>
        );
      })}
      {showAllClear && findings.length === 0 ? (
        <p className={css.phrase}>All clear. Keep writing.</p>
      ) : null}
    </section>
  );
}
